import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import { FileDownload, TableChart } from "@mui/icons-material";
import { API_CONFIG } from "../../../config/environment";
import { useAuth } from "../../../context/context";

const REPORTS = [
  {
    id: "products-excel",
    title: "Exportar Productos",
    description:
      "Descarga un archivo Excel con todos los datos actuales de los productos de venta usados.",
    endpoint: `${API_CONFIG.BIENES_URL}/products/export/excel`,
    method: "GET",
    filename: "productos-venta-usados.xlsx",
    icon: <TableChart sx={{ fontSize: 40, color: "success.main" }} />,
    tag: "Excel",
    tagColor: "success",
  },
];

export const ReportsList = () => {
  const { token } = useAuth();
  const [downloading, setDownloading] = useState({});
  const [errors, setErrors] = useState({});

  const handleDownload = async (report) => {
    setDownloading((prev) => ({ ...prev, [report.id]: true }));
    setErrors((prev) => ({ ...prev, [report.id]: null }));

    try {
      const response = await fetch(report.endpoint, {
        method: report.method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Error al generar el reporte (${response.status}: ${response.statusText})`,
        );
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = report.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`❌ Error descargando reporte "${report.title}":`, error);
      setErrors((prev) => ({
        ...prev,
        [report.id]: error.message || "Error inesperado al descargar el reporte",
      }));
    } finally {
      setDownloading((prev) => ({ ...prev, [report.id]: false }));
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Reportes disponibles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Genera y descarga reportes con la información actualizada del sistema.
      </Typography>

      <Grid container spacing={3}>
        {REPORTS.map((report) => (
          <Grid item xs={12} sm={6} md={4} key={report.id}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                  {report.icon}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {report.title}
                    </Typography>
                    <Chip
                      label={report.tag}
                      color={report.tagColor}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {report.description}
                </Typography>
                {errors[report.id] && (
                  <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrors((prev) => ({ ...prev, [report.id]: null }))}>
                    {errors[report.id]}
                  </Alert>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    downloading[report.id] ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <FileDownload />
                    )
                  }
                  onClick={() => handleDownload(report)}
                  disabled={downloading[report.id]}
                  fullWidth
                >
                  {downloading[report.id] ? "Descargando..." : "Descargar"}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
